import {
  Loader2,
  Maximize2,
  Mic,
  MicOff,
  Minimize2,
  Moon,
  RefreshCw,
  Sparkles,
  Sun,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { Resizable } from 're-resizable';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AceEditor from 'react-ace';
import Draggable from 'react-draggable';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAudioVisualization, useSpeechRecognition, useTextToSpeech } from '../../hooks/audio';
import { useAudioNarration } from '../../hooks/streaming';
import useNavigationGuard from '../../hooks/useNavigationGuard';
import axios from '../../utils/axiosConfig';
import { splitSentences } from '../../utils/sentenceSplitter';
import { readSSEStream } from '../../utils/sseStream';
import { getTechnicalCorrections } from '../../utils/transcriptionUtils';
import TutorialTooltip from '../DailyDigest/components/TutorialTooltip';
import TutorialRestartButton from '../TutorialRestartButton';
import { MOCK_INTERVIEW_TUTORIAL_STEPS } from './config/mockInterviewTutorialConfig';

// Import Ace Editor and its dependencies
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/ext-searchbox';
import 'ace-builds/src-noconflict/mode-java';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/snippets/java';
import 'ace-builds/src-noconflict/snippets/javascript';
import 'ace-builds/src-noconflict/snippets/python';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/theme-tomorrow';

// Add more language imports
import 'ace-builds/src-noconflict/mode-c_cpp';
import 'ace-builds/src-noconflict/mode-csharp';
import 'ace-builds/src-noconflict/mode-ruby';
import 'ace-builds/src-noconflict/mode-typescript';
import 'ace-builds/src-noconflict/snippets/c_cpp';
import 'ace-builds/src-noconflict/snippets/csharp';
import 'ace-builds/src-noconflict/snippets/ruby';
import 'ace-builds/src-noconflict/snippets/typescript';
import 'ace-builds/src-noconflict/theme-tomorrow';

// Define styles for stage transition notification
const stageTransitionNotificationStyle = `
.stage-transition-notification {
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(59, 130, 246, 0.9);
    color: white;
    padding: 10px 20px;
    border-radius: 8px;
    font-weight: 500;
    z-index: 1000;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    animation: notification-fade 3s ease-in-out;
}

@keyframes notification-fade {
    0% { opacity: 0; transform: translate(-50%, -20px); }
    10% { opacity: 1; transform: translate(-50%, 0); }
    90% { opacity: 1; transform: translate(-50%, 0); }
    100% { opacity: 0; transform: translate(-50%, -20px); }
}
`;

const MockInterview = () => {
  const { accessToken } = useAuth();
  const { problemName } = useParams();
  const decodedProblemName = decodeURIComponent(problemName);
  const nodeRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [problem, setProblem] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const isRecordingRef = useRef(false);
  const [conversation, setConversation] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('python');
  const conversationRef = useRef(null);
  const [interviewId, setInterviewId] = useState(null);
  const [panelDimensions, setPanelDimensions] = useState({
    width: 400,
    height: 600,
  });
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState(() => {
    // Calculate initial position at center of screen
    const footerHeight = 60;
    const headerHeight = 64;
    const availableHeight = window.innerHeight - footerHeight - headerHeight;
    const x = Math.max(0, (window.innerWidth - 400) / 2); // 400 is the initial width, center horizontally
    const y = Math.max(0, (availableHeight - 600) / 2); // Center in available space between header and footer
    return { x, y };
  });

  // Add bounds state that updates with panel dimensions
  const [dragBounds, setDragBounds] = useState(() => {
    const footerHeight = 60;
    const headerHeight = 64;
    const availableHeight = window.innerHeight - footerHeight - headerHeight;
    return {
      left: 0,
      top: 0,
      right: Math.max(0, window.innerWidth - 400),
      bottom: Math.max(0, availableHeight - 600),
    };
  });
  const canvasRef = useRef(null);
  const [code, setCode] = useState(() => {
    // Try to load saved code from localStorage for the current language
    try {
      if (selectedLanguage) {
        const savedCodeKey = `code_${problemName}_${selectedLanguage}`;
        const savedLanguageCode = localStorage.getItem(savedCodeKey);
        if (savedLanguageCode) {
          console.log(
            `Loaded saved code for ${problemName} in ${selectedLanguage}, length: ${savedLanguageCode.length}`
          );
          return savedLanguageCode;
        }
      }
    } catch (error) {
      console.error('Error loading code from localStorage:', error);
    }
    return ''; // Will be replaced with template once problem loads
  });
  const [isThinking, setIsThinking] = useState(false);
  const [problemConstraintsVisible, setProblemConstraintsVisible] = useState(false);

  // ==== Text-to-Speech (TTS) =============================
  // Allow users to turn audio responses on/off and track when the AI is speaking
  const [speechEnabled, setSpeechEnabled] = useState(false);

  // Add AbortController for managing streaming requests
  const streamingControllerRef = useRef(null);

  const [showTutorial, setShowTutorial] = useState(() => {
    return localStorage.getItem('hasSeenMockInterviewTutorial') !== 'true';
  });
  const [currentTutorialStep, setCurrentTutorialStep] = useState(0);

  // Add refs for tutorial nodes
  const nodeRefs = {
    'mode-selection': useRef(null),
    'problem-description': useRef(null),
    'code-editor': useRef(null),
    'interview-panel': useRef(null),
  };

  // Language mapping for Ace Editor modes
  const languageMap = {
    python: { mode: 'python', label: 'Python' },
    javascript: { mode: 'javascript', label: 'JavaScript' },
    java: { mode: 'java', label: 'Java' },
    cpp: { mode: 'c_cpp', label: 'C++' },
    csharp: { mode: 'csharp', label: 'C#' },
    typescript: { mode: 'typescript', label: 'TypeScript' },
    ruby: { mode: 'ruby', label: 'Ruby' },
  };

  // Word corrections come from the shared transcription utils, which already
  // exported getTechnicalCorrections() for this module — it was just never
  // imported. The local copy this replaces carried the same defect the shared
  // one used to: it lowercased the entire answer before re-capitalizing only
  // sentence starts, so every proper noun the candidate said was flattened
  // ("I use Redis on Kubernetes" -> "I use redis on kubernetes").
  const technicalCorrections = useMemo(() => getTechnicalCorrections(), []);

  // Waveform rendering, the microphone stream and the AudioContext all live in
  // useAudioVisualization, whose defaults match what was inlined here (32 bars,
  // blue-500, 25%-80% height). The old fftSize of 256 is deliberately NOT
  // carried over: it left each bar averaging two FFT bins, which is what made
  // the waveform jitter.
  const visualization = useAudioVisualization({ canvasRef, isRecording });

  // Live transcription. The hook is a superset of what was inlined here: same
  // auto-restart and same error handling, plus `isSupported` and steadier
  // cross-browser handling of "recognition has already started".
  const speechRecognition = useSpeechRecognition({
    wordCorrections: technicalCorrections,
    onError: setError,
  });
  const transcript = speechRecognition.transcript;
  const speechRecognitionStatus = speechRecognition.status;

  // Auto-stop timer for the three-minute recording cap.
  const recordingTimeoutRef = useRef(null);

  // Add this new function to process the problem content
  const processContent = (content) => {
    // If constraints should be hidden, remove the constraints section
    if (!problemConstraintsVisible) {
      content = content.replace(/(<p>\s*<strong>Constraints:<\/strong>\s*<\/p>[\s\S]*?<\/ul>)/, '');
    }

    return (
      content
        // Remove excessive newlines and whitespace
        .replace(/\n\s*\n\s*\n/g, '\n\n')
        // Remove whitespace between HTML tags
        .replace(/>\s+</g, '><')
        // Fix spacing around code blocks
        .replace(/<pre>/g, '<pre class="my-4">')
        // Add proper spacing for paragraphs
        .replace(/<p>/g, '<p class="mb-4">')
        // Process code tags
        .replace(
          /<code>/g,
          '<code class="bg-blue-50 px-1.5 py-0.5 rounded-sm text-blue-600 text-sm">'
        )
        // Fix list spacing
        .replace(/<ul>/g, '<ul class="list-disc pl-6 mb-4 space-y-2">')
    );
  };

  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('editorTheme') === 'monokai';
  });

  // Add a new state for the interview initialization modal
  const [showModeSelectionModal, setShowModeSelectionModal] = useState(true);

  // Modify interviewMode state to not have a default value and add related states
  const [interviewMode, setInterviewMode] = useState(null);
  const [currentStage, setCurrentStage] = useState('clarification');
  const [codingEnabled, setCodingEnabled] = useState(true);

  // Add interview stages for guided mode
  const interviewStages = [
    { id: 'clarification', label: 'Clarification' },
    { id: 'brute_force', label: 'Brute Force' },
    { id: 'optimal_solution', label: 'Optimal Solution' },
    { id: 'coding', label: 'Coding' },
    { id: 'testing', label: 'Testing' },
    { id: 'feedback', label: 'Feedback' },
  ];

  // Add feedback state
  const [showFeedback, setShowFeedback] = useState(false);

  // An interview exists from the moment the backend issues an id and is
  // over once feedback is on screen -- past that point there is nothing
  // left to lose by navigating away.
  useNavigationGuard({
    when: Boolean(interviewId) && !showFeedback,
    message:
      'Leaving now ends this interview. Your transcript and progress will be lost. Are you sure?',
  });
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);


  // Add state for text input at line ~68 with the other state definitions
  const [textInput, setTextInput] = useState('');

  // Add speech recognition status tracking

  // Auto-scroll conversation as it updates, including when the transcript changes
  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [conversation, transcript, isRecording]);

  // Update drag bounds when panel dimensions change
  useEffect(() => {
    const footerHeight = 60; // Approximate footer height
    const headerHeight = 64; // Header height from the UI
    const availableHeight = window.innerHeight - footerHeight - headerHeight;

    setDragBounds({
      left: 0,
      top: 0,
      right: Math.max(0, window.innerWidth - panelDimensions.width - 20), // 20px buffer to keep resize handle visible
      bottom: Math.max(0, availableHeight - panelDimensions.height - 20), // Account for footer and header
    });
  }, [panelDimensions]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  // Fetch problem details from LeetCode via our backend proxy
  useEffect(() => {
    const fetchProblemDetails = async () => {
      try {
        // First, try to get our internal leetcode ID
        const internalResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/v1/leetcode-question/by-name/${encodeURIComponent(
            decodedProblemName
          )}`
        );

        // Axios throws on non-2xx, so a successful response here means the problem exists
        const internalData = internalResponse.data;
        const internalLeetcodeId = internalData.leetcode_id;

        // Then get the problem details from LeetCode
        const query = `
                    query questionData($titleSlug: String!) {
                        question(titleSlug: $titleSlug) {
                            questionId
                            title
                            content
                            difficulty
                            codeSnippets {
                                lang
                                langSlug
                                code
                            }
                        }
                    }
                `;

        const titleSlug = decodedProblemName.toLowerCase().replace(/\s+/g, '-');
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/v1/leetcode-question/proxy`,
          {
            query,
            variables: { titleSlug },
          }
        );

        const data = response.data;

        if (data.errors) {
          throw new Error(data.errors[0].message || 'Failed to fetch problem details');
        }

        if (!data.data?.question) {
          throw new Error('Problem not found');
        }

        // Process the content before setting it
        const originalContent = data.data.question.content;
        const processedQuestion = {
          ...data.data.question,
          originalContent, // Store the original content
          content: processContent(originalContent),
          internalLeetcodeId, // Add our internal ID
        };

        setProblem(processedQuestion);
        setLoading(false);

        // More personalized initial message using InternHub cat character
        setConversation([
          {
            role: 'interviewer',
            message: `Hello! I'm the InternHub Cat, your AI interviewer today. We'll be solving the ${decodedProblemName} problem together! Would you like me to explain the problem, or would you prefer to read through it yourself first? Feel free to ask any clarifying questions - I'm here to help! 😺`,
          },
        ]);
      } catch (error) {
        console.error('Error fetching problem:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchProblemDetails();
  }, [decodedProblemName]);

  // Add new function to handle interview start with selected mode
  const startInterviewWithMode = (mode) => {
    setInterviewMode(mode);
    setShowModeSelectionModal(false);

    // Initialize UI based on selected mode
    if (mode === 'guided') {
      setCurrentStage('clarification');
      setProblemConstraintsVisible(false);
      setCodingEnabled(false);
    } else {
      setProblemConstraintsVisible(true);
      setCodingEnabled(true);
    }

    // Auto-advance tutorial if currently on mode selection step
    if (showTutorial && currentTutorialStep === 0) {
      setCurrentTutorialStep(1);
    }
  };

  // Update the startInterviewSession to use the selected mode
  const startInterviewSession = async (leetcodeId) => {
    try {
      // Check trial access for AI mock interviews
      try {
        const trialResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/v1/trial/check-access/ai_mock_interview`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!trialResponse.data.has_access) {
          throw new Error(
            "Basic trial doesn't allow AI mock interviews. Ask your university to get InternHub to unlock this feature."
          );
        }
      } catch (trialError) {
        if (trialError.response?.status === 403) {
          throw new Error(
            "Basic trial doesn't allow AI mock interviews. Ask your university to get InternHub to unlock this feature."
          );
        }
        // If it's a different error, continue (might be a regular user)
        console.warn('Could not check trial access:', trialError);
      }

      // Get userID from localStorage and ensure it's properly formatted
      const userId = localStorage.getItem('userID');
      if (!userId) {
        throw new Error('User ID not found. Please log in again.');
      }

      const parsedUserId = parseInt(userId);
      if (isNaN(parsedUserId)) {
        throw new Error('Invalid user ID format');
      }

      // Ensure interview mode is selected
      if (!interviewMode) {
        throw new Error('Please select an interview mode before starting');
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/mock-interview/start`,
        {
          leetcode_id: leetcodeId,
          user_id: parsedUserId,
          interview_mode: interviewMode,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = response.data;
      setInterviewId(data.interview_id);
      return data.interview_id;
    } catch (error) {
      console.error('Error starting interview:', error);
      setError(error.message);
      throw error;
    }
  };

  // Auto-scroll effect for new messages
  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [conversation]);


  // Save code to localStorage whenever it changes
  useEffect(() => {
    if (code && problemName) {
      localStorage.setItem(`code_${problemName}`, code);
    }
  }, [code, problemName]);

  // Update the load initial code template effect
  useEffect(() => {
    if (problem?.codeSnippets && selectedLanguage) {
      // Try to load language-specific saved code
      const savedCodeKey = `code_${problemName}_${selectedLanguage}`;
      const savedLanguageCode = localStorage.getItem(savedCodeKey);

      if (savedLanguageCode) {
        console.log(
          `Found saved code for ${selectedLanguage}, length: ${savedLanguageCode.length}`
        );
        setCode(savedLanguageCode);
        return;
      }

      // If no saved code, use the template for this language
      const snippet = problem.codeSnippets.find((s) => s.langSlug === selectedLanguage);
      if (snippet) {
        console.log(`Using template for ${selectedLanguage}`);
        setCode(snippet.code);
        // Save this as the starting point for this language
        localStorage.setItem(savedCodeKey, snippet.code);
      }
    }
  }, [problem, selectedLanguage, problemName]);

  // Update the handleCodeChange function to only save language-specific code
  const handleCodeChange = (newCode) => {
    setCode(newCode);
    console.log(`Code updated for ${selectedLanguage}, new length: ${newCode.length}`);

    // Store in localStorage - only for the current language
    try {
      if (selectedLanguage) {
        const savedCodeKey = `code_${problemName}_${selectedLanguage}`;
        localStorage.setItem(savedCodeKey, newCode);
      }
    } catch (error) {
      console.error('Error saving code to localStorage:', error);
    }

    // Store last edit timestamp to track coding activity
    if (interviewMode === 'guided' && currentStage === 'coding') {
      const timestamp = new Date().getTime();
      localStorage.setItem(`code_timestamp_${problemName}`, timestamp.toString());
    }
  };

  // Add useEffect to monitor code changes for active coding detection
  useEffect(() => {
    if (interviewMode === 'guided' && currentStage === 'coding' && interviewId) {
      // Set up interval to check for code changes
      const codeMonitorInterval = setInterval(() => {
        const lastEditTimestamp = localStorage.getItem(`code_timestamp_${problemName}`);
        if (lastEditTimestamp) {
          const currentTime = new Date().getTime();
          const timeSinceLastEdit = currentTime - parseInt(lastEditTimestamp);

          // If user hasn't edited code in 30 seconds while in coding stage,
          // ask AI for a code review prompt
          if (timeSinceLastEdit > 30000 && !isRecording && !isThinking) {
            console.log('Detected period of code inactivity, could prompt for code review');
            // This could trigger a notification or automatic prompt to the user
          }
        }
      }, 10000); // Check every 10 seconds

      return () => clearInterval(codeMonitorInterval);
    }
  }, [interviewMode, currentStage, interviewId, isRecording, isThinking, problemName]);

  // ---------------------------------------------------------------------
  // Audio narration
  //
  // The queue, sequencer and duration-synced reveal used to be inlined here
  // as a near-verbatim copy of the behavioral interview's. Both now drive the
  // shared engine; only the content policy (stripping ```json blocks) and the
  // stage_transition event differ, and those stay in the SSE loop below.
  // ---------------------------------------------------------------------
  const tts = useTextToSpeech({ enabled: speechEnabled, voice: 'nova', speed: 1 });

  // The message the current turn is narrating into.
  const narratingMessageIdRef = useRef(null);

  const handleNarrationUpdate = useCallback((update) => {
    const messageId = narratingMessageIdRef.current;
    if (messageId == null) return;

    setConversation((currentConversation) =>
      currentConversation.map((msg) =>
        msg._id === messageId
          ? {
              ...msg,
              message: update.content,
              sentences: update.sentences,
              currentDisplayIndex: update.currentDisplayIndex,
            }
          : msg
      )
    );
  }, []);

  const narration = useAudioNarration({
    generateAudio: tts.generateAudio,
    onUpdate: handleNarrationUpdate,
  });

  // Update the handleTextInputSubmit function to properly handle the interviewId setup
  const handleTextInputSubmit = async (e) => {
    e.preventDefault();

    if (!textInput.trim()) return;

    const userMessage = textInput.trim();
    setTextInput(''); // Clear the input field

    // First, ensure we have an interview session
    if (!interviewId) {
      try {
        if (!problem || !problem.internalLeetcodeId) {
          throw new Error('Problem details not loaded yet. Please wait and try again.');
        }

        // Get the ID directly from the function rather than relying on state update
        const newInterviewId = await startInterviewSession(problem.internalLeetcodeId);

        // Now use the returned ID directly instead of waiting for state to update
        await processUserInputWithId(userMessage, newInterviewId);
      } catch (error) {
        console.error('Error in handleTextInputSubmit:', error);
        setError(error.message);
      }
    } else {
      // If we already have an ID, use it
      await processUserInputAndGetResponse(userMessage);
    }
  };

  // Add a helper function that accepts an explicit interviewId
  const processUserInputWithId = async (userMessage, specificInterviewId) => {
    try {
      // Get current code state for context (but don't show in conversation)
      const currentCode = code || '';
      const currentLanguage = selectedLanguage || 'python';

      // Add only the user's message to the visible conversation (no code)
      setConversation((prev) => [
        ...prev,
        {
          role: 'candidate',
          message: userMessage, // Only show the user's actual message
        },
      ]);

      // Show thinking state
      setIsThinking(true);

      // More detailed logging for debugging
      console.log('=== Code Submission Debug ===');
      console.log(`Code submission length: ${currentCode.length}`);
      console.log(`Code submission preview: ${currentCode.substring(0, 100)}...`);
      console.log(`Code submission language: ${currentLanguage}`);
      console.log(`Using interview ID: ${specificInterviewId}`);

      // Create request body - send code as context but don't display it
      const requestBody = {
        user_response: userMessage,
        current_code: currentCode, // This still sends code to the backend
        language: currentLanguage,
        current_stage: interviewMode === 'guided' ? currentStage : null,
        problem_content: problem
          ? {
              title: problem.title,
              content: problem.content,
              difficulty: problem.difficulty,
            }
          : null,
      };

      console.log('Request body size:', JSON.stringify(requestBody).length, 'chars');
      console.log('Code included in request:', !!requestBody.current_code);

      // Cancel any existing streaming request
      if (streamingControllerRef.current) {
        try {
          streamingControllerRef.current.abort();
          console.log('Streaming request aborted');
        } catch (error) {
          console.error('Error aborting streaming request:', error);
        }
      }

      // Create new AbortController for this request
      streamingControllerRef.current = new AbortController();

      // Start streaming from the backend with abort signal
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/mock-interview/${specificInterviewId}/response-stream`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
          signal: streamingControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }

      const assistantMessageId = Date.now();

      if (speechEnabled) {
        // AUDIO MODE: Complex logic with sentences and audio
        await handleAudioMode(response, assistantMessageId);
      } else {
        // NON-AUDIO MODE: Simple character-by-character reveal
        await handleNonAudioMode(response, assistantMessageId);
      }

      // If speech is disabled we already handled text reveal; nothing else needed
    } catch (error) {
      // Don't show error if it was an abort
      if (error.name !== 'AbortError') {
        console.error('Error processing response:', error);
        setError(`Failed to process response: ${error.message}`);
        setConversation((prev) => [
          ...prev,
          {
            role: 'interviewer',
            message:
              "I'm sorry, there was a problem processing your response. Could you try again?",
          },
        ]);
      } else {
        console.log('Streaming request aborted');
      }
    } finally {
      setIsThinking(false);
    }
  };

  // Add back the processUserInputAndGetResponse function right after processUserInputWithId
  // Rename and generalize the function from processAudioAndGetResponse to processUserInputAndGetResponse
  // Modified processUserInputAndGetResponse to include current stage and problem content
  const processUserInputAndGetResponse = async (userMessage) => {
    try {
      // Get current code state for context (but don't show in conversation)
      const currentCode = code || '';
      const currentLanguage = selectedLanguage || 'python';

      // Add only the user's message to the visible conversation (no code)
      setConversation((prev) => [
        ...prev,
        {
          role: 'candidate',
          message: userMessage, // Only show the user's actual message
        },
      ]);

      // Show thinking state
      setIsThinking(true);

      // More detailed logging for debugging
      console.log('=== Code Submission Debug ===');
      console.log(`Code submission length: ${currentCode.length}`);
      console.log(`Code submission preview: ${currentCode.substring(0, 100)}...`);
      console.log(`Code submission language: ${currentLanguage}`);
      console.log(`Using interview ID: ${interviewId}`);

      // Create request body - send code as context but don't display it
      const requestBody = {
        user_response: userMessage,
        current_code: currentCode, // This still sends code to the backend
        language: currentLanguage,
        current_stage: interviewMode === 'guided' ? currentStage : null,
        problem_content: problem
          ? {
              title: problem.title,
              content: problem.content,
              difficulty: problem.difficulty,
            }
          : null,
      };

      console.log('Request body size:', JSON.stringify(requestBody).length, 'chars');
      console.log('Code included in request:', !!requestBody.current_code);

      // Cancel any existing streaming request
      if (streamingControllerRef.current) {
        try {
          streamingControllerRef.current.abort();
          console.log('Streaming request aborted');
        } catch (error) {
          console.error('Error aborting streaming request:', error);
        }
      }

      // Create new AbortController for this request
      streamingControllerRef.current = new AbortController();

      // Start streaming from the backend with abort signal
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/mock-interview/${interviewId}/response-stream`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
          signal: streamingControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }

      const assistantMessageId = Date.now();

      if (speechEnabled) {
        // AUDIO MODE: Complex logic with sentences and audio
        await handleAudioMode(response, assistantMessageId);
      } else {
        // NON-AUDIO MODE: Simple character-by-character reveal
        await handleNonAudioMode(response, assistantMessageId);
      }

      // If speech is disabled we already handled text reveal; nothing else needed
    } catch (error) {
      // Don't show error if it was an abort
      if (error.name !== 'AbortError') {
        console.error('Error processing response:', error);
        setError(`Failed to process response: ${error.message}`);
        setConversation((prev) => [
          ...prev,
          {
            role: 'interviewer',
            message:
              "I'm sorry, there was a problem processing your response. Could you try again?",
          },
        ]);
      } else {
        console.log('Streaming request aborted');
      }
    } finally {
      setIsThinking(false);
    }
  };

  // New function to handle stage transitions based on explicit signals
  const handleStageTransition = (nextStage) => {
    // Since backend now validates and auto-corrects stage transitions,
    // we can trust that the stage is valid
    console.log(`Processing stage transition: ${currentStage} -> ${nextStage}`);
    setCurrentStage(nextStage);

    // Configure UI based on next stage
    switch (nextStage) {
      case 'clarification':
        setProblemConstraintsVisible(false);
        setCodingEnabled(false);
        break;
      case 'brute_force':
      case 'optimal_solution':
        setProblemConstraintsVisible(true);
        setCodingEnabled(false);
        break;
      case 'coding':
      case 'testing':
        setProblemConstraintsVisible(true);
        setCodingEnabled(true);
        break;
      case 'feedback':
        // When transitioning to feedback stage, automatically end the interview
        setProblemConstraintsVisible(true);
        setCodingEnabled(true);

        // Add a short delay before ending the interview to allow the user to see the transition
        setTimeout(() => {
          // End the interview and request feedback
          endInterview();
        }, 1500);
        break;
      default:
        break;
    }

    // Show stage transition notification
    const notification = document.createElement('div');
    notification.className = 'stage-transition-notification';
    notification.textContent = `Moving to ${
      interviewStages.find((s) => s.id === nextStage)?.label || nextStage
    } stage`;

    // For feedback stage, change the notification text
    if (nextStage === 'feedback') {
      notification.textContent = 'Interview complete! Generating feedback...';
    }

    document.body.appendChild(notification);

    // Remove notification after animation
    setTimeout(() => {
      notification.remove();
    }, 3000);
  };

  // Update useEffect to set initial UI state based on interview mode
  useEffect(() => {
    if (interviewMode === 'guided') {
      // In guided mode, initially hide constraints and disable coding
      if (currentStage === 'clarification') {
        setProblemConstraintsVisible(false);
        setCodingEnabled(false);
      } else if (currentStage === 'coding' || currentStage === 'testing') {
        setProblemConstraintsVisible(true);
        setCodingEnabled(true);
      }
    } else {
      // In realistic mode, everything is visible and enabled
      setProblemConstraintsVisible(true);
      setCodingEnabled(true);
    }
  }, [interviewMode, currentStage]);

  // Toggle recording state
  const toggleRecording = async () => {
    // First, ensure we have an interview session
    if (!interviewId) {
      try {
        if (!problem || !problem.internalLeetcodeId) {
          throw new Error('Problem details not loaded yet. Please wait and try again.');
        }
        await startInterviewSession(problem.internalLeetcodeId);
      } catch (error) {
        console.error('Error in toggleRecording:', error);
        setError(error.message);
        return;
      }
    }

    if (isRecording) {
      // Capture the transcript before stopping — the hook clears it on restart.
      const finalTranscript = speechRecognition.transcript;

      speechRecognition.stop();
      visualization.cleanup();
      setIsRecording(false);

      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
        recordingTimeoutRef.current = null;
      }

      if (finalTranscript.trim()) {
        await processUserInputAndGetResponse(finalTranscript);
      }
      return;
    }

    try {
      // Silence the interviewer rather than talking over the candidate. This
      // used to call window.speechSynthesis.cancel(), which stopped the browser
      // TTS this module no longer uses — so in practice nothing was stopped.
      narration.stop();

      speechRecognition.reset();

      // Acquires the microphone. useAudioVisualization owns the only
      // getUserMedia call now: the MediaRecorder that used to hold a second
      // stream recorded into nothing (no ondataavailable handler, no consumer)
      // and has been removed.
      const { success } = await visualization.initialize();
      if (!success) {
        setError('Failed to access microphone. Please check your permissions.');
        return;
      }

      if (!speechRecognition.start()) {
        visualization.cleanup();
        setError('Could not start speech recognition. Please try again.');
        return;
      }

      setIsRecording(true);

      // Stop automatically after three minutes, as before.
      recordingTimeoutRef.current = setTimeout(
        () => {
          if (isRecordingRef.current) toggleRecording();
        },
        3 * 60 * 1000
      );
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Failed to start recording: ' + error.message);
    }
  };

  // Update the end of the interview when component unmounts
  useEffect(() => {
    return () => {
      if (interviewId) {
        axios
          .post(`${process.env.REACT_APP_API_URL}/api/v1/mock-interview/${interviewId}/end`, {
            end_time: new Date().toISOString(),
          })
          .catch(console.error);
      }
    };
  }, [interviewId]);

  // Update handleLanguageChange to simply switch to the saved code for the selected language
  const handleLanguageChange = (newLanguage) => {
    // First save current code for current language
    if (selectedLanguage) {
      try {
        const currentSavedCodeKey = `code_${problemName}_${selectedLanguage}`;
        localStorage.setItem(currentSavedCodeKey, code);
        console.log(`Saved current code for ${selectedLanguage}, length: ${code.length}`);
      } catch (error) {
        console.error('Error saving current language code:', error);
      }
    }

    // Update the selected language
    setSelectedLanguage(newLanguage);

    // Check if we already have the problem loaded
    if (problem?.codeSnippets) {
      // Try to load saved code for the new language
      const savedCodeKey = `code_${problemName}_${newLanguage}`;
      const savedLanguageCode = localStorage.getItem(savedCodeKey);

      if (savedLanguageCode) {
        // If we have saved code for this language, use it
        console.log(`Loading saved code for ${newLanguage}, length: ${savedLanguageCode.length}`);
        setCode(savedLanguageCode);
      } else {
        // If no saved code, use the template
        const snippet = problem.codeSnippets.find((s) => s.langSlug === newLanguage);
        if (snippet) {
          console.log(`Loading template for ${newLanguage}`);
          setCode(snippet.code);
          // Save this template as the starting point for this language
          localStorage.setItem(savedCodeKey, snippet.code);
        }
      }
    }
  };

  // Handle theme toggle
  const handleThemeToggle = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('editorTheme', newTheme ? 'monokai' : 'tomorrow');
  };

  // Add effect to handle body scroll during tutorial
  useEffect(() => {
    if (showTutorial) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showTutorial]);

  const handleNextStep = () => {
    // Don't allow progression past mode selection until modal is dismissed
    if (currentTutorialStep === 0 && showModeSelectionModal) {
      return; // Block progression
    }

    setCurrentTutorialStep((prev) => prev + 1);
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    localStorage.setItem('hasSeenMockInterviewTutorial', 'true');
  };

  const TutorialOverlay = () => {
    if (!showTutorial) return null;

    const currentStep = MOCK_INTERVIEW_TUTORIAL_STEPS[currentTutorialStep];
    const currentRef = nodeRefs[currentStep.id];
    const rect = currentRef?.current?.getBoundingClientRect();

    if (!rect) return null;

    const getTooltipPosition = () => {
      const windowWidth = window.innerWidth;
      const isSmallScreen = windowWidth < 1024;

      if (isSmallScreen) {
        return {
          top: rect.bottom + 20,
          left: rect.left,
          transform: 'none',
          width: Math.min(300, rect.width) + 'px',
          '--arrow-orientation': 'up',
        };
      }

      // Special handling for mode-selection step (center placement)
      if (currentStep.placement === 'center') {
        return {
          top: rect.bottom + 20,
          left: rect.left + rect.width / 2 - 150,
          width: '300px',
          '--arrow-orientation': 'up',
        };
      }

      // Adjust position based on placement
      if (currentStep.placement === 'right') {
        return {
          top: rect.top + rect.height / 2 - 100,
          left: rect.right + 20,
          width: '300px',
          '--arrow-orientation': 'left',
        };
      } else if (currentStep.placement === 'left') {
        return {
          top: rect.top + rect.height / 2 - 300,
          left: rect.left - 320,
          width: '300px',
          '--arrow-orientation': 'right',
        };
      }

      return {
        top: rect.bottom + 20,
        left: rect.left,
        width: '300px',
        '--arrow-orientation': 'up',
      };
    };

    return (
      <>
        <div className="fixed inset-0 z-50">
          {rect && (
            <div
              className="absolute bg-transparent"
              style={{
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
                borderRadius: '0.5rem',
              }}
            />
          )}
        </div>

        <TutorialTooltip
          step={currentStep}
          steps={MOCK_INTERVIEW_TUTORIAL_STEPS}
          onNext={handleNextStep}
          onComplete={handleTutorialComplete}
          style={getTooltipPosition()}
          nextButtonDisabled={currentTutorialStep === 0 && showModeSelectionModal}
        />
      </>
    );
  };

  // Add function to request interview feedback
  const requestFeedback = async () => {
    try {
      if (!interviewId) return;

      setFeedbackLoading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/mock-interview/${interviewId}/feedback`,
        {}
      );

      const data = response.data;
      setFeedbackData(data);
      setShowFeedback(true);
    } catch (error) {
      console.error('Error generating feedback:', error);
      setError(`Failed to generate feedback: ${error.message}`);
    } finally {
      setFeedbackLoading(false);
    }
  };

  // Add function to end the interview
  const endInterview = async () => {
    try {
      if (!interviewId) return;

      // First, make sure recording is stopped and resources are released.
      // visualization.cleanup() stops the media tracks and closes the
      // AudioContext, which is all this used to do by hand.
      if (isRecording) {
        speechRecognition.stop();
        visualization.cleanup();
        setIsRecording(false);
      }

      // End the interview session
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/mock-interview/${interviewId}/end`,
        {
          end_time: new Date().toISOString(),
        }
      );

      // Request feedback
      requestFeedback();
    } catch (error) {
      console.error('Error ending interview:', error);
    }
  };

  // Add FeedbackModal component
  const FeedbackModal = () => {
    if (!showFeedback || !feedbackData) return null;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Interview Feedback</h2>
              <button
                onClick={() => setShowFeedback(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Overall Scores */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[
                {
                  label: 'Technical Proficiency',
                  value: feedbackData.technical_rating,
                },
                {
                  label: 'Communication',
                  value: feedbackData.communication_rating,
                },
                {
                  label: 'Problem Solving',
                  value: feedbackData.problem_solving_rating,
                },
              ].map((metric) => (
                <div key={metric.label} className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-xl font-bold text-blue-600">{metric.value}/5</div>
                  <div className="text-gray-600 text-sm">{metric.label}</div>
                </div>
              ))}
            </div>

            {/* Strengths */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Strengths</h3>
              <ul className="list-disc pl-5 space-y-2">
                {feedbackData.strengths.map((strength, index) => (
                  <li key={index} className="text-gray-700">
                    {strength}
                  </li>
                ))}
              </ul>
            </div>

            {/* Areas for Improvement */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Areas for Improvement</h3>
              <ul className="list-disc pl-5 space-y-2">
                {feedbackData.areas_for_improvement.map((area, index) => (
                  <li key={index} className="text-gray-700">
                    {area}
                  </li>
                ))}
              </ul>
            </div>

            {/* Overall Feedback */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Overall Assessment</h3>
              <p className="text-gray-700 whitespace-pre-line">{feedbackData.overall_feedback}</p>
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 flex justify-end">
            <button
              onClick={() => setShowFeedback(false)}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add style tag to document head
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.type = 'text/css';
    styleElement.appendChild(document.createTextNode(stageTransitionNotificationStyle));
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Add the mode selection modal component
  const ModeSelectionModal = () => {
    if (!showModeSelectionModal || !problem) return null;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div
          ref={nodeRefs['mode-selection']}
          className="bg-white rounded-xl shadow-2xl sm:max-w-4xl m-3 sm:mx-auto"
        >
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Choose Interview Mode</h2>
            <p className="mt-2 text-gray-600">
              Select the type of mock interview experience you want for {problem.title}
            </p>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 gap-6">
              {/* Guided Mode Option */}
              <div
                onClick={() => startInterviewWithMode('guided')}
                className="border border-gray-200 rounded-lg p-5 hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer"
              >
                <div className="flex items-start">
                  <div className="bg-blue-100 rounded-full p-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-blue-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">Guided Interview</h3>
                    <p className="mt-1 text-gray-600">
                      A structured interview that guides you through each stage of the technical
                      interview process:
                    </p>
                    <ul className="mt-2 text-sm text-gray-600 list-disc pl-5 space-y-1">
                      <li>Problem clarification</li>
                      <li>Brute force solution</li>
                      <li>Optimal solution discussion</li>
                      <li>Coding implementation</li>
                      <li>Testing and review</li>
                    </ul>
                    <p className="mt-2 text-sm text-blue-600">
                      Recommended for beginners or those looking to learn proper interview structure
                    </p>
                  </div>
                </div>
              </div>

              {/* Realistic Mode Option */}
              <div
                onClick={() => startInterviewWithMode('realistic')}
                className="border border-gray-200 rounded-lg p-5 hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer"
              >
                <div className="flex items-start">
                  <div className="bg-green-100 rounded-full p-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-green-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">Realistic Interview</h3>
                    <p className="mt-1 text-gray-600">
                      A more authentic interview experience that mimics real-world technical
                      interviews:
                    </p>
                    <ul className="mt-2 text-sm text-gray-600 list-disc pl-5 space-y-1">
                      <li>Open-ended format</li>
                      <li>No forced progression</li>
                      <li>Interviewer adapts to your approach</li>
                      <li>Test your natural interview skills</li>
                    </ul>
                    <p className="mt-2 text-sm text-green-600">
                      Best for experienced candidates preparing for real interviews
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Cleanup on component unmount.
  //
  // The hooks own their own teardown — useSpeechRecognition stops the
  // recogniser and useAudioVisualization releases the stream and closes the
  // AudioContext — so this only has to stop the narration, which outlives a
  // turn and would otherwise keep talking after the component is gone.
  useEffect(() => {
    return () => {
      narration.stop();
      if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add effect to reprocess content when visibility changes
  useEffect(() => {
    if (problem?.content) {
      // Store the original content if not already stored
      if (!problem.originalContent) {
        setProblem((prev) => ({
          ...prev,
          originalContent: prev.content,
        }));
      }
      // Process from original content to avoid compounding effects
      const processedContent = processContent(problem.originalContent || problem.content);
      setProblem((prev) => ({
        ...prev,
        content: processedContent,
      }));
    }
  }, [problemConstraintsVisible, problem?.content]);

  // Add a function to reset code to template
  const resetCodeToTemplate = () => {
    if (problem?.codeSnippets && selectedLanguage) {
      const snippet = problem.codeSnippets.find((s) => s.langSlug === selectedLanguage);
      if (snippet) {
        // Confirm with the user
        if (
          window.confirm(
            `Reset your ${languageMap[selectedLanguage].label} code to the original template? This cannot be undone.`
          )
        ) {
          console.log(`Resetting code to ${selectedLanguage} template`);
          setCode(snippet.code);

          // Also update in localStorage
          try {
            const savedCodeKey = `code_${problemName}_${selectedLanguage}`;
            localStorage.setItem(savedCodeKey, snippet.code);
          } catch (error) {
            console.error('Error saving reset template to localStorage:', error);
          }
        }
      } else {
        setError(`No template available for ${languageMap[selectedLanguage].label}`);
      }
    }
  };

  // ======== Helper to finalise assistant message on premature stream end ========
  const finalizeAssistantMessage = (assistantMessageId, text, warning = true) => {
    const cleaned = removeJsonBlocks(text || '').trim();
    const finalText = cleaned.length > 0 ? cleaned : '[Response ended unexpectedly]';

    setConversation((prev) =>
      prev.map((msg) =>
        msg._id === assistantMessageId
          ? {
              ...msg,
              message: warning
                ? `${finalText}\n\n_(connection lost, response may be incomplete)_`
                : finalText,
              isStreaming: false,
            }
          : msg
      )
    );
  };

  // Simple non-audio mode: just reveal characters one by one
  const handleNonAudioMode = async (response, assistantMessageId) => {
    let fullText = '';
    let receivedStreamEnd = false; // NEW

    // Add empty message
    setConversation((currentConversation) => [
      ...currentConversation,
      {
        role: 'interviewer',
        message: '',
        _id: assistantMessageId,
        isStreaming: true,
        fullText: '',
        displayedCharIndex: 0,
      },
    ]);

    // Read all chunks first to get the full text
    try {
      await readSSEStream(response, (data) => {
        if (data.type === 'text_chunk') {
          fullText += data.content;

          // Update the message with new full text
          setConversation((currentConversation) =>
            currentConversation.map((msg) =>
              msg._id === assistantMessageId ? { ...msg, fullText: fullText } : msg
            )
          );
          return true;
        }

        if (data.type === 'stage_transition') {
          if (interviewMode === 'guided' && data.next_stage) {
            console.log('Stage transition event received:', data.next_stage);
            handleStageTransition(data.next_stage);
          }
          return true;
        }

        if (data.type === 'stream_end') {
          receivedStreamEnd = true; // NEW
          // Clean the text for display (remove JSON blocks)
          const cleanedDisplayText = removeJsonBlocks(fullText);

          if (cleanedDisplayText.trim().length === 0) {
            // Nothing received – finalise with placeholder
            finalizeAssistantMessage(assistantMessageId, '', false);
            return false;
          }
          // Start character-by-character reveal with cleaned text
          startCharacterReveal(assistantMessageId, cleanedDisplayText);

          // Still detect stage transitions from the full text
          if (interviewMode === 'guided') {
            const nextStage = detectStageTransition(fullText);
            if (nextStage) {
              console.log('Detected stage transition to', nextStage);
              handleStageTransition(nextStage);
            }
          }
          return false;
        }

        return true;
      });
    } finally {
      // If we exited without a proper stream_end, finalise with what we have
      if (!receivedStreamEnd) {
        finalizeAssistantMessage(assistantMessageId, fullText);
      }
    }
  };

  // Start revealing characters one by one for non-audio mode
  const startCharacterReveal = (assistantMessageId, fullText) => {
    if (fullText.trim().length === 0) {
      finalizeAssistantMessage(assistantMessageId, '', false);
      return;
    }
    let charIndex = 0;

    const revealNextChar = () => {
      if (charIndex < fullText.length) {
        charIndex++;

        setConversation((currentConversation) =>
          currentConversation.map((msg) =>
            msg._id === assistantMessageId
              ? {
                  ...msg,
                  message: fullText.substring(0, charIndex),
                  displayedCharIndex: charIndex,
                  isStreaming: charIndex < fullText.length,
                }
              : msg
          )
        );

        if (charIndex < fullText.length) {
          setTimeout(revealNextChar, 30); // 30ms per character
        }
      }
    };

    revealNextChar();
  };

  /**
   * Audio mode: stream the reply, hand each complete sentence to the narration
   * engine, and let it own synthesis, ordering and the synced text reveal.
   *
   * Sentence splitting goes through utils/sentenceSplitter. The previous inline
   * scan required `sentence.length > 10` but advanced past shorter ones anyway,
   * so a reply opening with "Sure." lost that word entirely — never displayed,
   * never spoken, and erased from `fullAssistantResponse`, which feeds stage
   * detection and the premature-end fallback. The splitter has no length
   * threshold and never discards a fragment.
   */
  const handleAudioMode = async (response, assistantMessageId) => {
    // Accumulated verbatim. Deliberately NOT rebuilt from the surviving
    // sentences the way the old implementation did: that rebuild is what
    // propagated dropped text into stage detection.
    let fullAssistantResponse = '';
    let textBuffer = '';
    let receivedStreamEnd = false;

    narratingMessageIdRef.current = assistantMessageId;
    narration.reset();

    // Add empty assistant message that the narration engine will fill in
    setConversation((currentConversation) => [
      ...currentConversation,
      {
        role: 'interviewer',
        message: '',
        _id: assistantMessageId,
        isStreaming: true,
        sentences: [],
      },
    ]);

    /** Queue a sentence unless it is entirely a ```json control block. */
    const narrate = (text) => {
      const clean = removeJsonBlocks(text);
      if (clean.length > 0) narration.enqueue(clean);
    };

    try {
      await readSSEStream(response, (data) => {
        if (data.type === 'text_chunk') {
          fullAssistantResponse += data.content;
          textBuffer += data.content;

          const { sentences: complete, rest } = splitSentences(textBuffer);
          textBuffer = rest;
          complete.forEach(narrate);
          return true;
        }

        if (data.type === 'stage_transition') {
          if (interviewMode === 'guided' && data.next_stage) {
            console.log('Stage transition event received:', data.next_stage);
            handleStageTransition(data.next_stage);
          }
          return true;
        }

        if (data.type === 'stream_end') {
          receivedStreamEnd = true;

          // Whatever is left in the buffer is the final sentence.
          const trailing = textBuffer.trim();
          if (trailing.length > 0) narrate(trailing);

          setConversation((currentConversation) =>
            currentConversation.map((msg) =>
              msg._id === assistantMessageId
                ? { ...msg, isStreaming: false, sentences: narration.getSentences() }
                : msg
            )
          );

          if (interviewMode === 'guided') {
            const nextStage = detectStageTransition(fullAssistantResponse);
            if (nextStage) {
              console.log('Detected stage transition to', nextStage);
              handleStageTransition(nextStage);
            }
          }
          return false;
        }

        if (data.type === 'error') {
          console.error('Stream error:', data.content);
          setError('Sorry, there was an error processing your request.');
          return false;
        }

        return true;
      });
    } finally {
      // Starts playback if it has not begun, and guarantees the transcript ends
      // up fully visible even when no audio ever arrived.
      narration.finish();

      if (!receivedStreamEnd) {
        console.warn('SSE connection closed before stream_end event');
        narration.stop();
        finalizeAssistantMessage(
          assistantMessageId,
          removeJsonBlocks(fullAssistantResponse + ' ' + textBuffer)
        );
      }
    }
  };

  // Add helper to detect stage transition JSON
  const detectStageTransition = (text) => {
    try {
      const match = text.match(/```json[\s\S]*?```/);
      if (match) {
        const jsonStr = match[0].replace(/```json|```/g, '').trim();
        const obj = JSON.parse(jsonStr);
        if (obj.stage_complete && obj.next_stage) {
          return obj.next_stage;
        }
      }
      // Fallback: look for simple {"stage":"value"}
      const simpleMatch = text.match(/\"stage\"\s*:\s*\"([a-z_]+)\"/i);
      if (simpleMatch) {
        const stageVal = simpleMatch[1];
        return stageVal;
      }
    } catch (e) {
      console.warn('Failed to parse stage transition JSON:', e);
    }
    return null;
  };

  // Function to remove JSON blocks from display text
  const removeJsonBlocks = (text) => {
    return text.replace(/```json[\s\S]*?```/g, '').trim();
  };

  // Add debug function to test stage transitions
  const debugForceStageTransition = async () => {
    if (!interviewId) return;

    if (currentStage === 'clarification') {
      setCurrentStage('brute_force');
    } else if (currentStage === 'brute_force') {
      setCurrentStage('optimal_solution');
    } else if (currentStage === 'optimal_solution') {
      setCurrentStage('coding');
    } else if (currentStage === 'coding') {
      setCurrentStage('testing');
    } else if (currentStage === 'testing') {
      setCurrentStage('feedback');
    } else if (currentStage === 'feedback') {
      // error message
      setError('Interview is already complete');
      return;
    }

    // Send a debug prompt that should trigger a stage transition
    const debugPrompt = `Please respond with exactly this JSON to test stage transition: \`\`\`json\n{"stage": "${currentStage}"}\n\`\`\`\nThen say 'This is a debug test for stage transition.'`;

    await processUserInputAndGetResponse(debugPrompt);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !problem) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-red-500 mb-4">{error || 'Failed to load problem'}</div>
        <button
          onClick={() => window.history.back()}
          className="px-4 py-2 bg-blue-500 text-white rounded-sm hover:bg-blue-600"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-50 relative">
      {/* Top Navigation/Header */}
      <div className="flex-none bg-white border-b border-gray-200 px-6 py-4 shadow-xs">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-gray-900">{problem.title}</h1>
            <span
              className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                problem.difficulty?.toLowerCase() === 'easy'
                  ? 'bg-green-100 text-green-800'
                  : problem.difficulty?.toLowerCase() === 'medium'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
              }`}
            >
              {problem.difficulty}
            </span>
          </div>

          {/* Display current mode instead of mode selector */}
          {interviewMode && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Mode:</span>
              <span
                className={`px-3 py-1 text-sm font-medium rounded-md ${
                  interviewMode === 'guided'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-green-100 text-green-800'
                }`}
              >
                {interviewMode === 'guided' ? 'Guided Interview' : 'Realistic Interview'}
              </span>
            </div>
          )}
        </div>

        {/* Interview Progress (only shown in guided mode) */}
        {interviewMode === 'guided' && (
          <div className="mt-4 flex items-center justify-between">
            <div className="w-full">
              <div className="flex justify-between mb-1">
                {interviewStages.map((stage) => (
                  <div
                    key={stage.id}
                    className={`text-xs font-medium ${
                      currentStage === stage.id
                        ? 'text-blue-600'
                        : interviewStages.findIndex((s) => s.id === stage.id) <
                            interviewStages.findIndex((s) => s.id === currentStage)
                          ? 'text-green-600'
                          : 'text-gray-500'
                    }`}
                  >
                    {stage.label}
                  </div>
                ))}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-blue-500 h-1.5 rounded-full"
                  style={{
                    width: `${
                      ((interviewStages.findIndex((s) => s.id === currentStage) + 1) /
                        interviewStages.length) *
                      100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Problem Content Area (Left + Center panels) */}
        <div className="flex-1 flex">
          {/* Left Panel - Problem Description */}
          <div
            ref={nodeRefs['problem-description']}
            className="w-1/2 overflow-auto border-r border-gray-200 bg-white"
          >
            <div className="p-6">
              <div
                className={`prose prose-sm max-w-none prose-pre:bg-gray-50 prose-pre:p-3 prose-pre:rounded-lg prose-code:bg-blue-50 prose-code:text-blue-600 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none [&_p]:text-gray-600 [&_li]:text-gray-600 transition-[filter] duration-300 ${
                  showModeSelectionModal ? 'blur-xs select-none' : ''
                }`}
                dangerouslySetInnerHTML={{
                  __html: problem.content,
                }}
              />

              {/* Constraints section - conditionally visible */}
              {!showModeSelectionModal && !problemConstraintsVisible && (
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h3 className="text-yellow-800 font-medium">Constraints Hidden</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    The constraints are hidden during the clarification stage. Ask the interviewer
                    about the constraints to gain a better understanding of the problem.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Center Panel - Code Editor */}
          <div className="w-1/2 border-r border-gray-200">
            {/* Language Selector */}
            <div className="flex items-center justify-end gap-4 p-2 bg-gray-50 border-b border-gray-200">
              <label className="text-sm font-medium text-gray-700">Language:</label>
              <select
                value={selectedLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-xs text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={interviewMode === 'guided' && !codingEnabled}
              >
                {Object.entries(languageMap).map(([key, { label }]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
              <button
                onClick={resetCodeToTemplate}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-xs text-sm hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center gap-2"
                disabled={interviewMode === 'guided' && !codingEnabled}
                title="Reset code to template"
              >
                <RefreshCw className="h-4 w-4" />
                Reset
              </button>
              <button
                onClick={handleThemeToggle}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-xs text-sm hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center gap-2"
              >
                {isDarkMode ? (
                  <>
                    <Sun className="h-4 w-4" />
                    <span>Switch to Light</span>
                  </>
                ) : (
                  <>
                    <Moon className="h-4 w-4" />
                    <span>Switch to Dark</span>
                  </>
                )}
              </button>
            </div>
            <div ref={nodeRefs['code-editor']} className="h-[calc(100%-41px)]">
              {!codingEnabled ? (
                <div className="h-full flex items-center justify-center bg-gray-100 p-6">
                  <div className="text-center max-w-md">
                    <img
                      src="/writingcar.png"
                      alt="InternHub Cat"
                      className="w-16 h-16 mx-auto mb-4"
                    />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Coding is disabled during this stage
                    </h3>
                    <p className="text-gray-600">
                      In the guided interview mode, you need to complete the clarification and
                      solution discussion stages before writing code.
                    </p>
                    <p className="text-gray-600 mt-4">
                      Current stage:{' '}
                      <strong>{interviewStages.find((s) => s.id === currentStage)?.label}</strong>
                    </p>
                  </div>
                </div>
              ) : (
                <AceEditor
                  mode={languageMap[selectedLanguage].mode}
                  theme={isDarkMode ? 'monokai' : 'tomorrow'}
                  name="code_editor"
                  width="100%"
                  height="100%"
                  fontSize={15}
                  showPrintMargin={false}
                  showGutter={true}
                  value={code}
                  onChange={handleCodeChange}
                  readOnly={!codingEnabled}
                  setOptions={{
                    enableBasicAutocompletion: true,
                    enableLiveAutocompletion: true,
                    enableSnippets: true,
                    showLineNumbers: true,
                    tabSize: 4,
                    useWorker: false,
                    fontFamily: "JetBrains Mono, 'Courier New', monospace",
                  }}
                  editorProps={{ $blockScrolling: true }}
                  style={{
                    fontFamily: "JetBrains Mono, 'Courier New', monospace",
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Draggable & Resizable Interview Panel */}
        <div className="absolute inset-0 pointer-events-none">
          <Draggable
            nodeRef={nodeRef}
            handle=".panel-handle"
            defaultPosition={position}
            bounds={dragBounds}
            position={position}
            onStop={(e, data) => setPosition({ x: data.x, y: data.y })}
          >
            <div
              ref={(el) => {
                nodeRef.current = el;
                if (nodeRefs['interview-panel'].current !== el) {
                  nodeRefs['interview-panel'].current = el;
                }
              }}
              className="absolute pointer-events-auto z-50"
              style={{ width: panelDimensions.width }}
            >
              {isMinimized ? (
                <div className="bg-blue-50 px-4 py-3 rounded-xl shadow-lg panel-handle cursor-move select-none">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img
                        src="/writingcar.png"
                        alt="InternHub Cat"
                        className="w-8 h-8 rounded-[20%] border-2 border-white shadow-xs"
                      />
                      <div>
                        <h3 className="font-semibold text-gray-900">Mock Interview Session</h3>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          with InternHub Cat AI <Sparkles className="h-3 w-3 text-blue-500" />
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setIsMinimized(false);
                        setPanelDimensions((prev) => ({
                          width: prev.width,
                          height: 600,
                        }));
                      }}
                      className="p-1 hover:bg-blue-100 rounded-lg transition-colors"
                      title="Maximize"
                    >
                      <Maximize2 className="h-5 w-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              ) : (
                <Resizable
                  size={panelDimensions}
                  minWidth={350}
                  minHeight={400}
                  maxWidth={800}
                  maxHeight={Math.min(window.innerHeight - 124 - 60, 800)} // 124px for header+margins, 60px for footer
                  onResizeStop={(e, direction, ref, d) => {
                    setPanelDimensions({
                      width: panelDimensions.width + d.width,
                      height: panelDimensions.height + d.height,
                    });
                  }}
                  enable={{
                    right: true,
                    bottom: true,
                    bottomRight: true,
                  }}
                  handleStyles={{
                    right: {
                      right: '0',
                      width: '8px',
                      cursor: 'e-resize',
                      height: '100%',
                    },
                    bottom: {
                      bottom: '0',
                      height: '8px',
                      width: '100%',
                      cursor: 's-resize',
                    },
                    bottomRight: {
                      right: '0',
                      bottom: '0',
                      width: '20px',
                      height: '20px',
                      cursor: 'se-resize',
                    },
                  }}
                >
                  <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden h-full flex flex-col relative group">
                    {/* Add resize indicators */}
                    <div className="absolute inset-0 pointer-events-none">
                      {/* Right resize indicator */}
                      <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex flex-col gap-[3px]">
                          {[...Array(3)].map((_, i) => (
                            <div key={i} className="w-[3px] h-[3px] bg-gray-500" />
                          ))}
                        </div>
                      </div>

                      {/* Bottom resize indicator */}
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex gap-[3px]">
                          {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-[3px] w-[3px] bg-gray-500" />
                          ))}
                        </div>
                      </div>

                      {/* Corner resize indicator */}
                      <div className="absolute bottom-0 right-0 w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M20 10H10V20H20V10Z" fill="#E5E7EB" />
                          <path d="M20 13H13V20H20V13Z" fill="#9CA3AF" />
                          <path d="M20 16H16V20H20V16Z" fill="#4B5563" />
                        </svg>
                      </div>
                    </div>
                    {/* Interview Header */}
                    <div className="flex-none bg-blue-50 px-4 py-3 panel-handle cursor-move select-none border-b border-blue-100 rounded-t-xl">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <img
                            src="/writingcar.png"
                            alt="InternHub Cat"
                            className="w-8 h-8 rounded-[20%] border-2 border-white shadow-xs"
                          />
                          <div>
                            <h3 className="font-semibold text-gray-900">Mock Interview Session</h3>
                            <p className="text-sm text-gray-600 flex items-center gap-1">
                              with InternHub Cat AI
                              <Sparkles className="h-3 w-3 text-blue-500" />
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Audio toggle button */}
                          <button
                            onClick={() => setSpeechEnabled(!speechEnabled)}
                            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${
                              speechEnabled
                                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            title={speechEnabled ? 'Turn off audio' : 'Turn on audio'}
                          >
                            {speechEnabled ? (
                              <>
                                <Volume2 className="h-3 w-3" />
                                <span>Audio</span>
                              </>
                            ) : (
                              <>
                                <VolumeX className="h-3 w-3" />
                                <span>Audio</span>
                              </>
                            )}
                          </button>

                          {/* Minimize button */}
                          <button
                            onClick={() => {
                              setIsMinimized(true);
                              setPanelDimensions((prev) => ({
                                width: prev.width,
                                height: 56,
                              }));
                            }}
                            className="p-1 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Minimize"
                          >
                            <Minimize2 className="h-5 w-5 text-gray-600" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Conversation Area */}
                    <div ref={conversationRef} className="flex-1 overflow-auto px-4 py-4 space-y-4">
                      {conversation.map((msg, index) => {
                        if (msg.role === 'candidate') {
                          return (
                            <div
                              key={index}
                              className="p-4 rounded-lg shadow-xs bg-gray-50 border border-gray-100 mr-4"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <p className="font-medium text-sm text-gray-700">You</p>
                              </div>
                              <p className="text-gray-600">{msg.message}</p>
                            </div>
                          );
                        } else if (msg.role === 'interviewer') {
                          return (
                            <div
                              key={msg._id || index}
                              className="p-4 rounded-lg shadow-xs bg-blue-50 border border-blue-100 ml-4"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <img
                                  src="/writingcar.png"
                                  alt="InternHub Cat"
                                  className="w-6 h-6 rounded-[20%]"
                                />
                                <p className="font-medium text-sm text-gray-700">InternHub Cat</p>
                                {msg.isStreaming && (
                                  <div className="flex items-center ml-2">
                                    <div className="flex space-x-1">
                                      <div
                                        className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"
                                        style={{
                                          animationDelay: '0ms',
                                          animationDuration: '1s',
                                        }}
                                      ></div>
                                      <div
                                        className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"
                                        style={{
                                          animationDelay: '200ms',
                                          animationDuration: '1s',
                                        }}
                                      ></div>
                                      <div
                                        className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"
                                        style={{
                                          animationDelay: '400ms',
                                          animationDuration: '1s',
                                        }}
                                      ></div>
                                    </div>
                                    <span className="text-xs text-blue-600 ml-2">
                                      responding...
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="text-gray-600">
                                {/* Non-audio mode: simple character-by-character reveal */}
                                {!speechEnabled && msg.fullText ? (
                                  <div className="leading-relaxed whitespace-pre-wrap">
                                    {msg.message}
                                  </div>
                                ) : speechEnabled && msg.sentences && msg.sentences.length > 0 ? (
                                  /* Audio mode: complex sentence structure with character animation */
                                  <div className="leading-relaxed whitespace-pre-wrap">
                                    {msg.sentences.map((sentence, sentenceIndex) => {
                                      const isVisible =
                                        sentenceIndex <= (msg.currentDisplayIndex ?? -1);

                                      return (
                                        <span
                                          key={sentenceIndex}
                                          className={`inline transition-opacity duration-75 ease-out ${
                                            isVisible ? 'opacity-100' : 'opacity-0'
                                          }`}
                                        >
                                          {/* Check if sentence has progressive character structure */}
                                          {sentence.characters && sentence.characters.length > 0 ? (
                                            <>
                                              {/* Progressive character reveal */}
                                              {sentence.characters.map((charObj, charIndex) => (
                                                <span
                                                  key={charIndex}
                                                  className={`inline transition-opacity duration-75 ease-out ${
                                                    charObj.isVisible ? 'opacity-100' : 'opacity-0'
                                                  }`}
                                                >
                                                  {charObj.char}
                                                </span>
                                              ))}
                                            </>
                                          ) : (
                                            // Fallback to sentence-level display
                                            sentence.text
                                          )}
                                          {/* Add space between sentences */}
                                          {sentenceIndex < msg.sentences.length - 1 && ' '}
                                        </span>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <>
                                    {/* Fallback for messages without special structure */}
                                    {msg.message && (
                                      <span className="whitespace-pre-wrap leading-relaxed">
                                        {msg.message}
                                      </span>
                                    )}
                                    {msg.isStreaming && !msg.message && (
                                      <span className="text-gray-400 italic">
                                        Preparing response...
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                      {isRecording && transcript && (
                        <div className="p-4 rounded-lg shadow-xs bg-green-50 border border-green-100 mr-4 animate-pulse">
                          <p className="font-medium text-sm mb-1 text-gray-700">You (speaking):</p>
                          <p className="text-gray-600">{transcript}</p>
                        </div>
                      )}
                    </div>

                    {/* Recording Controls */}
                    <div className="flex-none border-t border-gray-200 p-4 bg-white">
                      {/* Speech Recognition Status Indicator */}
                      {isRecording && (
                        <div className="mb-3 flex items-center justify-center">
                          <div
                            className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-2 ${
                              speechRecognitionStatus === 'listening'
                                ? 'bg-green-100 text-green-800'
                                : speechRecognitionStatus === 'no-speech'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : speechRecognitionStatus === 'error'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            <div
                              className={`w-2 h-2 rounded-full ${
                                speechRecognitionStatus === 'listening'
                                  ? 'bg-green-500 animate-pulse'
                                  : speechRecognitionStatus === 'no-speech'
                                    ? 'bg-yellow-500'
                                    : speechRecognitionStatus === 'error'
                                      ? 'bg-red-500'
                                      : 'bg-gray-400'
                              }`}
                            />
                            <span>
                              {speechRecognitionStatus === 'listening'
                                ? 'Listening...'
                                : speechRecognitionStatus === 'no-speech'
                                  ? 'Waiting for speech...'
                                  : speechRecognitionStatus === 'error'
                                    ? 'Speech recognition error'
                                    : 'Initializing...'}
                            </span>
                          </div>
                        </div>
                      )}

                      <canvas
                        ref={canvasRef}
                        width={300}
                        height={60}
                        className="w-[300px] h-[60px] mb-4 rounded-lg border border-gray-200 mx-auto"
                      />

                      {/* Add text input form */}
                      <form onSubmit={handleTextInputSubmit} className="mb-4">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={textInput}
                            onChange={(e) => setTextInput(e.target.value)}
                            placeholder="Type your response instead..."
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                            disabled={isRecording || isThinking}
                          />
                          <button
                            type="submit"
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            disabled={isRecording || isThinking || !textInput.trim()}
                          >
                            Send
                          </button>
                        </div>
                      </form>

                      <div className="flex flex-col space-y-2">
                        <button
                          onClick={toggleRecording}
                          className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                            isRecording
                              ? 'bg-red-500 hover:bg-red-600 shadow-red-100'
                              : 'bg-blue-500 hover:bg-blue-600 shadow-blue-100'
                          } text-white shadow-lg`}
                        >
                          {isRecording ? (
                            <>
                              <MicOff className="h-5 w-5" />
                              Stop Recording
                            </>
                          ) : (
                            <>
                              <Mic className="h-5 w-5" />
                              Start Recording
                            </>
                          )}
                        </button>

                        {/* Debug Stage Transition Button (only in guided mode) */}
                        {interviewMode === 'guided' && interviewId && !isRecording && (
                          <button
                            onClick={debugForceStageTransition}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-yellow-500 hover:bg-yellow-600 text-white shadow-lg"
                            disabled={isThinking}
                          >
                            🐛 Debug: Force Stage Transition
                          </button>
                        )}

                        {/* End Interview Button */}
                        {interviewId && !isRecording && (
                          <button
                            onClick={endInterview}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium bg-gray-800 hover:bg-gray-900 text-white shadow-lg"
                            disabled={feedbackLoading}
                          >
                            {feedbackLoading ? (
                              <>
                                <Loader2 className="h-5 w-5 animate-spin" />
                                Generating Feedback...
                              </>
                            ) : (
                              <>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                End Interview & Get Feedback
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </Resizable>
              )}
            </div>
          </Draggable>
        </div>
      </div>

      {/* Tutorial Overlay */}
      <TutorialOverlay />

      {/* Tutorial Restart Button */}
      <TutorialRestartButton tutorialKey="hasSeenMockInterviewTutorial" />

      {/* Feedback Modal */}
      <FeedbackModal />

      {/* Mode Selection Modal */}
      <ModeSelectionModal />
    </div>
  );
};

export default MockInterview;
